import React from 'react';
import { message, Spin } from 'antd';
import moment from 'moment';

import {
  ENTITY_NAME,
  ENTITY_PARENTNAME,
  CUSTOM_FIELD_DATE_FORMAT,
  PENDING_DATE_FORMAT,
  SEARCH_DATE_FORMAT,
  sendingDateFormat,
} from '../constants/appConfig';
import { setLocalStorage } from './storageUtil';

import CustomLoader from '../assets/finpos_loader.gif';
import { fetch } from './httpUtil';
import CryptoJS from 'crypto-js';
import pbkdf2 from 'pbkdf2';
import jwtDecode from 'jwt-decode';
import { SETTLEMENT_STATUS } from './enum';
import APP_CONFIG from '../constants/appConfig';

// Custom Spinner
export const antIcon = <img src={APP_CONFIG.loader} width={36} alt="loading" />;
Spin.setDefaultIndicator(antIcon);

export const toUpper = (v, prev) => {
  if (v === prev) {
    return v;
  }
  return v && v.charAt(0).toUpperCase() + v.slice(1);
};

export const urlToList = url => {
  if (url) {
    const urlList = url.split('/').filter(i => i);
    return urlList.map((urlItem, index) => `/${urlList.slice(0, index + 1).join('/')}`);
  }
};

export const isEmpty = obj => {
  if (Array.isArray(obj)) {
    if (obj?.length > 0) return false;
  } else {
    for (var key in obj) {
      if (obj.hasOwnProperty(key)) return false;
    }
  }
  return true;
};

export const stringExplode = (str, delimiter) => {
  return str.split(delimiter);
};

export const isDuplicate = (array, title) => {
  return array.some(el => el.title === title);
};

export const arrayCompare = (arr1, arr2) => {
  if (!arr1 || !arr2) return;
  let result;
  arr1.forEach((e1, i) =>
    arr2.forEach(e2 => {
      if (e1.length > 1 && e2.length) {
        result = arrayCompare(e1, e2);
      } else if (e1 !== e2) {
        result = false;
      } else {
        result = true;
      }
    })
  );
  return result;
};

export const stringCompare = (str1, str2) => {
  const string1 = !isEmpty(str1) ? str1.toString() : '';
  const string2 = !isEmpty(str2) ? str2.toString() : '';
  return string1 === string2;
};

export const objectCompare = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
};

export const exactMatchByKey = (matchVal, myArray, matchKey = 'key') => {
  if (myArray) {
    for (var i = 0; i < myArray.length; i++) {
      if (myArray[i][matchKey] === matchVal) {
        return myArray[i];
      }
    }
  }
  return;
};

export const matchBySearchKey = (matchKey, arrayItems) => {
  if (arrayItems) {
    const currentList = arrayItems.filter(item => {
      return item.searchKey.toLowerCase().search(matchKey.toLowerCase()) !== -1;
    });
    return currentList;
  }
  return;
};

export const matchByDynamicKey = (matchKey, myArray) => {
  if (myArray) {
    for (var i = 0; i < myArray.length; i++) {
      if (myArray[i][matchKey]) {
        return myArray[i][matchKey];
      }
    }
  }
  return;
};

export const convertStingToPascalCase = str => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

export const setColorStyle = (str1, str2, oldData, colorCode = 'red') => {
  const parsedStr1 = JSON.stringify(str1);
  const parsedStr2 = JSON.stringify(str2);
  const isEqual = stringCompare(parsedStr1, parsedStr2);
  const styleCode = !isEmpty(oldData)
    ? isEqual
      ? { color: '' }
      : { color: colorCode }
    : { color: '' };
  return styleCode;
};

export const hasPermission = (permissionCode, permissions) => {
  return permissions && permissions.includes(permissionCode);
};

export const thousandSeparator = num => {
  let num_parts = num.toString().split('.');
  num_parts[0] = num_parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return num_parts.join('.');
};

export const getDate = () => {
  let date = new Date().toLocaleDateString();
  let d = new Date(date),
    month = '' + (d.getMonth() + 1),
    day = '' + d.getDate(),
    year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [year, month, day].join('-');
};

export const getTime = () => {
  let time = new Date().toLocaleTimeString('en-US', {
    hour12: false,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  });
  time = time.split(':');
  time = time.join('');
  return time;
};

export const getStatusLabel = value => {
  let label = '';
  const statusValue = JSON.stringify(value);
  statusValue === 'true' ? (label = 'YES') : (label = 'NO');
  return label;
};

export const getFieldPropertyValue = (data, compareKey, compareCode, returnKey) => {
  let result,
    obj = '';
  let elements = [];
  if (Array.isArray(compareKey)) {
    compareKey.map(key => {
      obj =
        Array.isArray(data) &&
        data.filter(d => {
          return key === d[`${compareCode}`];
        });
      if (obj && obj[0] !== undefined && obj[0] !== null) {
        elements.push(obj[0][`${returnKey}`]);
      }
    });
    result = elements.join(', ');
  } else {
    obj =
      Array.isArray(data) &&
      data.filter(d => {
        return compareKey === d[`${compareCode}`];
      });
    if (obj && obj[0] !== undefined && obj[0] !== null) {
      result = obj[0][`${returnKey}`];
    }
  }
  return result;
};

export const getNameFromList = (
  listData = [],
  listCompareKey = 'id',
  compareValue = 'id',
  returnKey = 'title'
) => {
  const filteredData = listData?.find(item => item?.[listCompareKey] === compareValue);

  return filteredData?.[returnKey] ?? '-';
};

export const getParentGroup = parentReference => {
  const lastIndex = parentReference?.lastIndexOf('/');
  return parentReference ? parentReference.substr(0, lastIndex) : '';
};

export const getSubMenu = path => {
  const menuPath = path.split('/', 4);
  return menuPath[menuPath.length - 1];
};

export const getSortingOrder = sorterOrdered => {
  let orderType;
  if (sorterOrdered) {
    if (sorterOrdered === 'descend') {
      orderType = 'desc';
    } else {
      orderType = 'asc';
    }
  }
  return orderType;
};

export const getFilterFieldValue = reportModel => {
  let filterFieldValues =
    reportModel &&
    reportModel.map((index, obj) => {
      if (index.condition && index.field && (index.value || index.fromDate || index.otherValue)) {
        var rObj = {};
        let fromDate;
        let toDate;
        const value = index.value;
        fromDate = moment(index.fromDate).format(SEARCH_DATE_FORMAT);
        toDate = moment(index.toDate).format(SEARCH_DATE_FORMAT);
        const otherValue = index.otherValue;

        if (!value) {
          rObj.dateValue = toDate;
          rObj.value = fromDate;
        } else {
          if (moment.isMoment(value)) {
            rObj.value = moment(value).format(SEARCH_DATE_FORMAT);
          } else {
            rObj.value = value;
            rObj.otherValue = otherValue;
          }
        }
        const condition = index.condition;
        const field = index.field;
        const customizable = index.customizable;

        rObj.condition = condition;
        rObj.field = field;
        rObj.customizable = customizable;
      }
      return rObj;
    });

  filterFieldValues = filterFieldValues?.filter(Boolean);
  return filterFieldValues;
};
export const validateDateFormat = searchParameter => {
  if (searchParameter && searchParameter.includes('/', 2)) {
    if (searchParameter.includes(':', 2)) {
      searchParameter = moment(searchParameter).format(PENDING_DATE_FORMAT);
    } else {
      searchParameter = moment(searchParameter).format(CUSTOM_FIELD_DATE_FORMAT);
    }
  }
  return searchParameter;
};

export const formatLongNumberWithSuffix = value => {
  if (value === 0) {
    return 0;
  } else {
    // hundreds
    if (value <= 999) {
      return value;
    }
    // thousands
    else if (value >= 1000 && value <= 999999) {
      return value / 1000 + 'K';
    }
    // millions
    else if (value >= 1000000 && value <= 999999999) {
      return value / 1000000 + 'M';
    }
    // billions
    else if (value >= 1000000000 && value <= 999999999999) {
      return value / 1000000000 + 'B';
    } else return value;
  }
};

export const hasTouched = isFieldsTouched => {
  return isFieldsTouched();
};

export const getFileType = file => {
  return typeof file === 'string';
};

export const hasErrors = fieldsError => {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
};

export const prepareValidationRules = rules => {
  let validationRules = [];
  rules &&
    rules.map(data => {
      switch (data.rule) {
        case 'minValue':
          validationRules.push({
            type: 'number',
            min: parseFloat(data.value),
            message: `${data.message}`,
          });
          break;
        case 'maxValue':
          validationRules.push({
            type: 'number',
            max: parseFloat(data.value),
            message: `${data.message} `,
          });
          break;
        case 'minLength':
          validationRules.push({
            min: parseInt(data.value),
            message: `${data.message} `,
          });
          break;
        case 'maxLength':
          validationRules.push({
            max: parseInt(data.value),
            message: `${data.message} `,
          });
          break;
      }
    });
  return validationRules;
};

export const checkActiveStatusFromDate = (effectiveFrom, effectiveTo) => {
  let fromMoment = moment(effectiveFrom, sendingDateFormat);
  let toMoment = moment(effectiveTo, sendingDateFormat);
  let todayMoment = moment(moment(new Date()).format(sendingDateFormat), sendingDateFormat);
  return fromMoment <= todayMoment && todayMoment <= toMoment;
};

export const findName = (checkValue, checkArray, checkKey, returnKey = () => {}) => {
  if (checkValue === '***') {
    return 'ALL';
  } else {
    let temp = exactMatchByKey(checkValue, checkArray, checkKey);
    if (temp) {
      return isEmpty(returnKey)
        ? temp.type ||
            temp.description ||
            temp.currencyName ||
            temp.name ||
            temp.ledgerName ||
            temp.displayName ||
            temp.labelName ||
            temp.title ||
            temp.heading
        : temp[returnKey];
    }
    return null;
  }
};

export const handleMaxLength = (rule, value, callback, maxLength, t) => {
  if (value === undefined || value === null || value === '') {
    callback();
  } else if (value.length > 100) {
    callback(t('maximum.character.length.validation', { max: maxLength }));
  } else {
    callback();
  }
};

export const handleRemitParameterCriterias = (item, operationType, appBuilders) => {
  let remitParameterArray = [];
  let remitInfoArray =
    appBuilders?.remitInfo &&
    appBuilders?.remitInfo.map(item => {
      return {
        fieldName: item.fieldName,
        displayName: item.displayName,
      };
    });
  if (operationType === 'SND') {
    remitParameterArray =
      appBuilders?.remitter &&
      appBuilders?.remitter.map(item => {
        return {
          fieldName: item.fieldName,
          displayName: item.displayName,
        };
      });
  } else {
    remitParameterArray =
      appBuilders?.beneficiary &&
      appBuilders?.beneficiary.map(item => {
        return {
          fieldName: item.fieldName,
          displayName: item.displayName,
        };
      });
  }
  remitParameterArray = remitParameterArray && remitParameterArray.concat(remitInfoArray);

  return findName(item, remitParameterArray, 'fieldName', 'displayName');
};

export const handleChangeLogLinkClick = (recordName, recordParentName = '') => {
  setLocalStorage(ENTITY_NAME, recordName);
  setLocalStorage(ENTITY_PARENTNAME, recordParentName);
};

export const draftDetailApproveOrRejectEntityName = (entityName, formData) => {
  const entity = formData.entity
    ? formData.entity?.includes('-')
      ? formData.entity.charAt(0).toUpperCase() + formData.entity.substr(1, entityName.length - 1)
      : formData.entity.substr(-2, 2) === 'es'
      ? formData.entity.charAt(0).toUpperCase() + formData.entity.substr(1, entityName.length - 3)
      : formData.entity.charAt(0).toUpperCase() + formData.entity.substr(1, entityName.length - 1)
    : entityName.charAt(0).toUpperCase() +
      (entityName.substr(-3, 3) === 'ies'
        ? `${entityName.substr(1, entityName.length - 4)}y`
        : entityName.substr(1, entityName.length - 1));
  return entity;
};

/**
 * Find and send MTO name from MTO list
 * @param {string} checkValue
 * @param {Array} checkArray
 *
 * @return {string} name
 */
export const findMtoName = (checkValue, checkArray) => {
  const mtoMatchByKey = (givenValue, givenArray) => {
    if (givenArray) {
      for (let i = 0; i < givenArray.length; i++) {
        if (
          givenArray[i]?.mtoDetail &&
          (givenArray[i].mtoDetail.mtoCode === givenValue ||
            givenArray[i].mtoRefCode === givenValue)
        ) {
          return givenArray[i];
        }
      }
    }
    return undefined;
  };

  let temp = mtoMatchByKey(checkValue, checkArray);

  if (temp) {
    return (
      temp.type ||
      temp.description ||
      temp.currencyName ||
      temp.name ||
      temp.ledgerName ||
      temp.displayName
    );
  }
  return undefined;
};

/**
 * Refactors amount according to currency decimal value
 * @param {string} amount
 * @param {string} currencyIsoAlpha
 * @param {Array} currencies
 *
 * @return {float} refactoredAmount || {string} amount
 */
export const handleCurrencyDecimal = (amount, currencyIsoAlpha, currencies) => {
  let foundCurrencyObj = exactMatchByKey(currencyIsoAlpha, currencies, 'isoAlpha');
  let refactoredAmount =
    foundCurrencyObj &&
    !isEmpty(foundCurrencyObj) &&
    parseFloat(amount).toFixed(foundCurrencyObj.decimalValue);
  return refactoredAmount || amount;
};

// trim initial spaces
export const trimInput = ({ target }, setFieldsValue) => {
  const { id, value } = target;
  setFieldsValue({ [id]: value.trim() });
};

// format numbers using international format with trailing zeros
// eg: 123456789-> 123,456,789.00
export const internationalFormattedAmount = (number, comma = true) => {
  const num = parseFloat(number);
  let formattedNumber;
  try {
    if (isNaN(parseFloat(num))) {
      return '-';
    }
    if (comma) {
      formattedNumber = new Intl.NumberFormat('en-UK').format(parseFloat(num.toFixed(2)));
    } else {
      formattedNumber = num.toFixed(2);
    }

    if (Number.isInteger(num)) {
      return `${num.toLocaleString()}.00`;
    }

    const splitted = formattedNumber.split('.');

    if (splitted[1].length < 2) {
      return `${formattedNumber}0`;
    }
    return formattedNumber;
  } catch (error) {
    return '-';
  }
};

// format numbers using international format without trailing zeros
// eg: 123456789-> 123,456,789
export const internationalFormattedNumber = number => {
  try {
    if (isNaN(parseFloat(number))) {
      return '-';
    }
    const formattedNumber = new Intl.NumberFormat('en-UK').format(number);

    return formattedNumber;
  } catch (error) {
    return '-';
  }
};

// debounce with delay in ms

// debounching fetch
let timeout;

export function debounce(callback, timeout) {
  if (timeout) {
    clearTimeout(timeout);
    timeout = null;
  }

  function fetchList() {
    // call fn
    callback();
  }

  timeout = setTimeout(() => {
    fetchList();
  }, timeout);
}

function throttle(cb, delay = 1000) {
  let shouldWait = false;
  let waitingArgs;
  const timeoutFunc = () => {
    if (waitingArgs == null) {
      shouldWait = false;
    } else {
      cb(...waitingArgs);
      waitingArgs = null;
      setTimeout(timeoutFunc, delay);
    }
  };

  return (...args) => {
    if (shouldWait) {
      waitingArgs = args;
      return;
    }

    cb(...args);
    shouldWait = true;

    setTimeout(timeoutFunc, delay);
  };
}

export const allowOnlyNumber = evt => {
  let charCode = evt.which ? evt.which : evt.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    evt.preventDefault();
    return false;
  }
  return true;
};

export const allowOnlyAlphabet = e => {
  let charCode = e.which ? e.which : e.keyCode;
  if (charCode > 32 && (charCode < 65 || charCode > 90) && (charCode < 97 || charCode > 122)) {
    e.preventDefault();
    return false;
  }
  return true;
};

export const defaultColor = '#eee4ed';

export const defaultProvinceColor = [
  '#88c4cc',
  '#c4bcdc',
  '#969cb3',
  '#bbecf5',
  '#7bccec',
  '#7476b8',
  '#ccccff',
];

export const getRandomColor = () => {
  return (
    '#' +
    Math.random()
      .toString(16)
      .slice(-6)
  );
};

export const getBarGraphPayload = stateProps => {
  const { turnover, currency, startDate, year, terminalId } = stateProps;
  let payload = {};

  if (turnover === 'SEVEN_DAYS') {
    payload = {
      barGraphType: turnover,
      currencyCode: currency,
    };
    if (terminalId) {
      payload.terminalId = terminalId;
    }
    return payload;
  } else if (turnover === 'MONTHLY') {
    payload = {
      barGraphType: turnover,
      startDate: startDate,
      currencyCode: currency,
    };
    if (terminalId) {
      payload.terminalId = terminalId;
    }
    return payload;
  } else if (turnover === 'YEARLY') {
    if (year > moment().format('YYYY')) {
      message.error('Date cannot be in the future.');
    } else {
      payload = {
        barGraphType: turnover,
        startDate: year,
        currencyCode: currency,
      };
      if (terminalId) {
        payload.terminalId = terminalId;
      }
    }
    return payload;
  }
};

export const StatusCodeCheck = code => {
  if (code?.response?.status === undefined) {
    return false;
  } else {
    return true;
  }
};

export const generateUniqueId = () => {
  const timestamp = new Date().getTime();
  const random = Math.random()
    .toString(36)
    .substring(2, 11);
  return `${timestamp}-${random}`;
};

export const generateUniqueKey = (length = 62) => {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const targetLen = length;
  let key = '';

  const ACCEPT_THRESHOLD = 248;

  let buf = [];
  let i = 0;

  const refill = () => {
    const wa = CryptoJS.lib.WordArray.random(128);
    buf = [];
    const { words, sigBytes } = wa;
    for (let b = 0; b < sigBytes; b++) {
      buf.push((words[b >>> 2] >>> (24 - (b % 4) * 8)) & 0xff);
    }
    i = 0;
  };

  while (key.length < targetLen) {
    if (i >= buf.length) refill();
    const byte = buf[i++];
    if (byte < ACCEPT_THRESHOLD) {
      key += alphabet[byte % 62];
    }
  }

  return key;
};

export const getMapApiKey = async () => {
  try {
    const response = await fetch(`auths/v1/auth/get-map-key`);
    if (response?.status === 200) {
      return response;
    } else {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  } catch (error) {
    message.error('Failed:', error);
  }
};

const SALT_LENGTH = 16;
const IV_LENGTH = 16;
const KEY_LENGTH = 256;
const ITERATIONS = 65536;

function deriveKey(password, salt) {
  const derivedKey = pbkdf2.pbkdf2Sync(
    password,
    Buffer.from(salt, 'hex'),
    ITERATIONS,
    KEY_LENGTH / 8,
    'sha256'
  );
  return CryptoJS.enc.Hex.parse(derivedKey.toString('hex'));
}

export function decryptData(encryptedData, password) {
  const decodedData = CryptoJS.enc.Base64.parse(encryptedData).toString(CryptoJS.enc.Hex);

  const saltHex = decodedData.substr(0, SALT_LENGTH * 2);
  const ivHex = decodedData.substr(SALT_LENGTH * 2, IV_LENGTH * 2);
  const ciphertextHex = decodedData.substr((SALT_LENGTH + IV_LENGTH) * 2);

  const salt = CryptoJS.enc.Hex.parse(saltHex);
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const ciphertext = CryptoJS.enc.Hex.parse(ciphertextHex);

  const key = deriveKey(password, salt.toString(CryptoJS.enc.Hex));

  const decrypted = CryptoJS.AES.decrypt({ ciphertext: ciphertext }, key, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });

  return decrypted.toString(CryptoJS.enc.Utf8);
}

const getToken = apiKey => {
  return (
    apiKey?.request?.rqProxyXhr?._requestHeaders?.['X-XSRF-TOKEN'] ||
    apiKey?.config?.headers?.['X-XSRF-TOKEN']
  );
};

export const fetchMapApiKey = async () => {
  try {
    const apiKey = await getMapApiKey();
    if (apiKey?.headers?.['x-xsrf-token']) {
      const token = getToken(apiKey);
      if (token) {
        const decoded = jwtDecode(token);
        const encryptedData = apiKey?.data?.message;
        const lastAccessTime = decoded['LAST-ACCESS-TIME'];
        const googleMapsApiKey = decryptData(encryptedData, lastAccessTime);
        return { googleMapsApiKey, loading: false };
      } else {
        message.error('Token not found in request headers');
      }
    } else {
      message.error('Token not found in API response');
    }
  } catch (error) {
    message.error(`Error fetching API key: ${error.message}`);
  }
  return { googleMapsApiKey: null, loading: false };
};

export function areArraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item, index) => JSON.stringify(item) === JSON.stringify(arr2[index]));
}

export const formatStatus = status => {
  return status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());
};

export const getStatusColor = status => {
  if (!status) return { color: 'inherit', text: '-' };

  const statusUpper = status.toUpperCase();

  switch (statusUpper) {
    case SETTLEMENT_STATUS.PARTIALLY_SETTLED:
    case SETTLEMENT_STATUS.UNIDENTIFIED:
      return { color: '#FF8000', text: formatStatus(status) };

    case SETTLEMENT_STATUS.MOVED:
      return { color: '#A600FFFF', text: formatStatus(status) };
    case SETTLEMENT_STATUS.DRAFT:
    case SETTLEMENT_STATUS.PENDING:
      return { color: '#585861', text: formatStatus(status) };

    case SETTLEMENT_STATUS.NEW:
      return { color: '#00B7FA', text: formatStatus(status) };

    case SETTLEMENT_STATUS.IN_PROGRESS:
    case SETTLEMENT_STATUS.IN_PROGRESS_ALT:
      return { color: '#E77B00', text: formatStatus(status) };

    case SETTLEMENT_STATUS.RETRYING:
      return { color: '#21005D', text: formatStatus(status) };

    case SETTLEMENT_STATUS.UNSETTLED:
    case SETTLEMENT_STATUS.REJECTED:
    case SETTLEMENT_STATUS.FAILURE:
    case SETTLEMENT_STATUS.FAILED:
    case SETTLEMENT_STATUS.FAIL:
      return { color: '#B3261E', text: formatStatus(status) };
    case SETTLEMENT_STATUS.SETTLED:
    case SETTLEMENT_STATUS.SUCCESS:
    case SETTLEMENT_STATUS.APPROVED:
      return { color: '#00CD35', text: formatStatus(status) };
    default:
      return { color: 'inherit', text: formatStatus(status) };
  }
};
export const getStatusBackgroundColor = status => {
  if (!status) return { backgroundColor: 'inherit', text: '-', color: '#000000' };

  const statusUpper = status.toUpperCase();

  switch (statusUpper) {
    case SETTLEMENT_STATUS.PARTIALLY_SETTLED:
    case SETTLEMENT_STATUS.UNIDENTIFIED:
      return { backgroundColor: '#FF8000', text: formatStatus(status), color: '#ffffff' };
    case SETTLEMENT_STATUS.DRAFT:
    case SETTLEMENT_STATUS.PENDING:
      return {
        backgroundColor: '#585861',
        text: formatStatus(status),
        color: '#ffffff',
      };

    case SETTLEMENT_STATUS.NEW:
      return {
        backgroundColor: '#00B7FA',
        text: formatStatus(status),
        color: '#ffffff',
      };

    case SETTLEMENT_STATUS.IN_PROGRESS:
    case SETTLEMENT_STATUS.IN_PROGRESS_ALT:
      return {
        backgroundColor: '#E77B00',
        text: formatStatus(status),
        color: '#ffffff',
      };

    case SETTLEMENT_STATUS.RETRYING:
      return {
        backgroundColor: '#21005D',
        text: formatStatus(status),
        color: '#ffffff',
      };

    case SETTLEMENT_STATUS.UNSETTLED:
    case SETTLEMENT_STATUS.REJECTED:
    case SETTLEMENT_STATUS.FAILURE:
    case SETTLEMENT_STATUS.FAILED:
    case SETTLEMENT_STATUS.FAIL:
      return { backgroundColor: '#B3261E', text: formatStatus(status), color: '#ffffff' };
    case SETTLEMENT_STATUS.SETTLED:
    case SETTLEMENT_STATUS.SUCCESS:
    case SETTLEMENT_STATUS.APPROVED:
      return { backgroundColor: '#00CD35', text: formatStatus(status), color: '#ffffff' };
    default:
      return { backgroundColor: 'inherit', text: formatStatus(status), color: '#000000' };
  }
};

export const isSettlementActionable = status => {
  if (!status) return false;

  const nonActionableStatus = [
    SETTLEMENT_STATUS.SETTLED,
    SETTLEMENT_STATUS.DRAFT,
    SETTLEMENT_STATUS.REJECTED,
    SETTLEMENT_STATUS.NEW,
    SETTLEMENT_STATUS.IN_PROGRESS,
    SETTLEMENT_STATUS.IN_PROGRESS_ALT,
    SETTLEMENT_STATUS.APPROVED,
  ];

  return !nonActionableStatus.includes(status.toUpperCase());
};
